import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AnimatedPage from "../../../components/AnimatedPage";
import { supportService, tradersService } from "../../../services";
import { SupportChat } from "../../../services/support/support.service";
import { Trader } from "../../../types/trader";
import { useNotification } from "../../../contexts/NotificationContext";
import SearchableSelect from "../../../components/SearchableSelect";

interface TraderSupportRef {
  handleOpenDialog: () => void;
}

const TraderSupport = forwardRef<TraderSupportRef>((props, ref) => {
  const [chats, setChats] = useState<SupportChat[]>([]);
  const [traders, setTraders] = useState<Trader[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterName, setFilterName] = useState("");
  const [filterTraderId, setFilterTraderId] = useState<string>("all");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingChat, setEditingChat] = useState<SupportChat | null>(null);
  const [newChatName, setNewChatName] = useState("");
  const [newChatTraderId, setNewChatTraderId] = useState<string>("");
  const [newChatTgId, setNewChatTgId] = useState<string>("");
  const { showNotification } = useNotification();

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [tradersData, chatsData] = await Promise.all([
        tradersService.getTraders({ is_archive: false }),
        supportService.getChats(),
      ]);

      setTraders(tradersData || []);
      setChats(chatsData || []);
    } catch (err: any) {
      console.error("Error loading support chats:", err);
      setError(
        err?.response?.data?.detail || "Ошибка при загрузке чатов поддержки",
      );
      showNotification("Ошибка при загрузке чатов поддержки", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResetFilters = () => {
    setFilterName("");
    setFilterTraderId("all");
  };

  const filteredChats = useMemo(() => {
    const nameQuery = filterName.trim().toLowerCase();

    return chats.filter((chat) => {
      const nameMatch =
        !nameQuery || chat.name.toLowerCase().includes(nameQuery);
      const traderMatch =
        filterTraderId === "all" || chat.trader_id === filterTraderId;
      return nameMatch && traderMatch;
    });
  }, [chats, filterName, filterTraderId]);

  useImperativeHandle(ref, () => ({
    handleOpenDialog: () => {
      setEditingChat(null);
      setNewChatName("");
      setNewChatTraderId("");
      setNewChatTgId("");
      setCreateDialogOpen(true);
    },
  }));

  return (
    <AnimatedPage>
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          gap={2}
          flexWrap="wrap"
        >
          <Typography variant="h5">Поддержка трейдеров</Typography>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <TextField
              label="Название чата"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            />
            <Box sx={{ minWidth: 220 }}>
              <SearchableSelect
                label="Трейдер"
                size="small"
                value={filterTraderId === 'all' ? '' : filterTraderId}
                options={traders.map((item) => ({ id: item.id, label: item.name }))}
                onChange={(value) => setFilterTraderId(value || 'all')}
              />
            </Box>
            <TextField
              type="button"
              value="Сбросить"
              onClick={handleResetFilters}
              size="small"
              sx={{
                minWidth: 120,
                "& input": { cursor: "pointer" },
              }}
              InputProps={{ readOnly: true }}
            />
          </Box>
        </Box>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="240px"
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Telegram Chat ID</TableCell>
                  <TableCell>Название</TableCell>
                  <TableCell>Трейдер</TableCell>
                  <TableCell align="left">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredChats.length ? (
                  filteredChats.map((chat) => (
                    <TableRow key={chat.id}>
                      <TableCell>{chat.id}</TableCell>
                      <TableCell>{chat.tg_chat_id}</TableCell>
                      <TableCell>{chat.name}</TableCell>
                      <TableCell>
                        {traders.find((t) => t.id === chat.trader_id)?.name ||
                          chat.trader_id ||
                          "-"}
                      </TableCell>
                      <TableCell align="left">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingChat(chat);
                            setNewChatName(chat.name);
                            setNewChatTraderId(chat.trader_id);
                            setNewChatTgId(String(chat.tg_chat_id));
                            setCreateDialogOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Чаты поддержки не найдены
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          disableRestoreFocus
          disableEnforceFocus
        >
          <DialogTitle>
            {editingChat ? "Редактировать чат поддержки" : "Добавить чат поддержки"}
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <TextField
                label="Название чата"
                value={newChatName}
                onChange={(e) => setNewChatName(e.target.value)}
                fullWidth
              />
              <TextField
                label="Telegram Chat ID"
                value={newChatTgId}
                onChange={(e) => setNewChatTgId(e.target.value)}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Трейдер</InputLabel>
                <Select
                  value={newChatTraderId}
                  label="Трейдер"
                  onChange={(event: SelectChangeEvent<string>) =>
                    setNewChatTraderId(event.target.value)
                  }
                >
                  {traders.map((trader) => (
                    <MenuItem key={trader.id} value={trader.id}>
                      {trader.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Отмена</Button>
            <Button
              variant="contained"
              disabled={!newChatName || !newChatTraderId || !newChatTgId}
              onClick={async () => {
                try {
                  const trimmed = newChatTgId.trim();
                  const tgIdPattern = /^-\d{13}$/;
                  if (!tgIdPattern.test(trimmed)) {
                    showNotification(
                      "Укажите корректный Telegram Chat ID (отрицательное 13-значное число)",
                      "error",
                    );
                    return;
                  }
                  const tgId = Number(trimmed);
                  if (editingChat) {
                    const updated = await supportService.updateChat(editingChat.id, {
                      tg_chat_id: tgId,
                      name: newChatName,
                      trader_id: newChatTraderId,
                    });
                    setChats((prev) =>
                      prev.map((chat) => (chat.id === updated.id ? updated : chat)),
                    );
                    showNotification("Чат поддержки успешно обновлён", "success");
                  } else {
                    const created = await supportService.createChat({
                      tg_chat_id: tgId,
                      name: newChatName,
                      trader_id: newChatTraderId,
                    });
                    setChats((prev) => [...prev, created]);
                    showNotification("Чат поддержки успешно создан", "success");
                  }
                  setCreateDialogOpen(false);
                  setEditingChat(null);
                } catch (err: any) {
                  console.error("Error saving support chat:", err);
                  showNotification("Ошибка при сохранении чата поддержки", "error");
                }
              }}
            >
              {editingChat ? "Сохранить" : "Создать"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AnimatedPage>
  );
});

export default TraderSupport;


